import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Bell, CheckCheck, Calendar, Trophy, CreditCard, Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const POLL_MS = 60000; // 60s

const typeIconMap = {
  tournament: Trophy,
  booking: Calendar,
  payment: CreditCard,
  zelle: CreditCard,
};

const formatTimeAgo = (iso) => {
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  } catch {
    return '';
  }
};

const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [marking, setMarking] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/notifications`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setItems(data.notifications || []);
        setUnread(data.unread_count || 0);
      } else if (res.status === 401 || res.status === 403) {
        // Not logged in — silently keep empty
        setItems([]);
        setUnread(0);
      }
    } catch (e) {
      // Network hiccup — ignore
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const id = setInterval(fetchNotifications, POLL_MS);
    return () => clearInterval(id);
  }, [fetchNotifications]);

  // Refresh when opening so the count is always fresh
  useEffect(() => {
    if (open) {
      setLoading(true);
      fetchNotifications().finally(() => setLoading(false));
    }
  }, [open, fetchNotifications]);

  const markAllRead = async () => {
    if (unread === 0 || marking) return;
    setMarking(true);
    try {
      await fetch(`${API_URL}/api/notifications/read-all`, {
        method: 'PUT',
        credentials: 'include',
      });
      setItems((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnread(0);
    } finally {
      setMarking(false);
    }
  };

  const markOneRead = async (notification_id) => {
    try {
      await fetch(`${API_URL}/api/notifications/${notification_id}/read`, {
        method: 'PUT',
        credentials: 'include',
      });
      setItems((prev) =>
        prev.map((n) => (n.notification_id === notification_id ? { ...n, read: true } : n))
      );
      setUnread((u) => Math.max(0, u - 1));
    } catch (e) { /* ignore */ }
  };

  const hasUnread = unread > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Notifications"
          data-testid="notification-bell-btn"
          className="relative p-2 rounded-full text-gray-600 hover:text-green-700 hover:bg-green-50 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500/40"
        >
          <Bell className={`w-5 h-5 ${hasUnread ? 'animate-[ring_2s_ease-in-out_infinite]' : ''}`} />
          {hasUnread && (
            <span
              data-testid="notification-bell-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-white"
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[360px] p-0 border-gray-200 shadow-xl"
        data-testid="notification-popover"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div>
            <h4 className="font-semibold text-gray-900 text-sm">Notifications</h4>
            <p className="text-xs text-gray-500">
              {hasUnread ? `${unread} unread` : 'All caught up'}
            </p>
          </div>
          {hasUnread && (
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllRead}
              disabled={marking}
              className="text-xs text-green-700 hover:bg-green-50 h-8"
              data-testid="notification-mark-all-read"
            >
              <CheckCheck className="w-3.5 h-3.5 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-[420px]">
          {loading && items.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center" data-testid="notification-empty-state">
              <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
                <Bell className="w-6 h-6 text-gray-400" />
              </div>
              <p className="text-sm font-medium text-gray-700">You're all caught up</p>
              <p className="text-xs text-gray-500 mt-1">New alerts will show up here.</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100" data-testid="notification-list">
              {items.map((n) => {
                const Icon = typeIconMap[n.type] || Info;
                const isUnread = !n.read;
                const row = (
                  <div
                    className={`flex gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                      isUnread ? 'bg-green-50/60' : ''
                    }`}
                    onClick={() => isUnread && markOneRead(n.notification_id)}
                    data-testid={`notification-item-${n.notification_id}`}
                  >
                    <div
                      className={`w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center ${
                        isUnread ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm leading-snug ${isUnread ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                          {n.title || 'Notification'}
                        </p>
                        {isUnread && (
                          <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0 mt-1.5" aria-label="unread" />
                        )}
                      </div>
                      {n.message && (
                        <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{n.message}</p>
                      )}
                      <p className="text-[11px] text-gray-400 mt-1">
                        {formatTimeAgo(n.created_at)}
                      </p>
                    </div>
                  </div>
                );
                // If notification has a link, wrap it; else plain div
                return (
                  <li key={n.notification_id}>
                    {n.link ? (
                      <Link to={n.link} onClick={() => setOpen(false)} className="block">
                        {row}
                      </Link>
                    ) : (
                      row
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </ScrollArea>
        {items.length > 0 && (
          <div className="px-4 py-2 border-t border-gray-100 text-center">
            <Link
              to="/dashboard"
              onClick={() => setOpen(false)}
              className="text-xs text-green-700 hover:underline"
              data-testid="notification-view-dashboard"
            >
              View all in Dashboard
            </Link>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
